import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaRobot, FaUser, FaUsers } from "react-icons/fa";
import { MenuActions } from "src/components/menu-actions";
import Modal from "src/components/modal/modal";
import { AiScenario } from "../entities/ai-scenario.entity";

type ScenarioRow = AiScenario & { id: string };

type Props = {
  data: ScenarioRow[];
};

const formatDate = (ts: any) => {
  if (!ts) return "N/A";
  return new Date(ts?.toDate?.() || ts).toLocaleDateString();
};

const formatTime = (ts: any) => {
  if (!ts) return "N/A";
  return new Date(ts?.toDate?.() || ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const truncateText = (value: string, maxLength = 100) => {
  if (!value) return "—";
  return value.length > maxLength ? `${value.slice(0, maxLength).trim()}...` : value;
};

const ScenarioDetailsModal: React.FC<{
  scenario: ScenarioRow | null;
  onClose: () => void;
}> = ({ scenario, onClose }) => {
  const roles = useMemo(() => scenario?.roles?.slice(0, 2) || [], [scenario]);

  return (
    <Modal isOpen={!!scenario} onClose={onClose} size={2}>
      {scenario && (
        <div className="space-y-6 pr-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI Custom Topic Details</h2>
            <p className="text-sm text-gray-500 mt-1">
              Inspect the user-created scenario without editing it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Scenario ID
              </p>
              <p className="font-mono text-sm text-gray-800 break-all">{scenario.id}</p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                User
              </p>
              <Link
                to={`/users/${scenario.userId}`}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 px-3 py-1.5 text-sm font-semibold text-white no-underline"
              >
                <FaUser />
                {scenario.userId}
              </Link>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
              Situation
            </p>
            <p className="text-sm leading-7 text-gray-800 whitespace-pre-wrap">
              {scenario.situation || "—"}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                Roles
              </p>
              <div className="flex flex-wrap gap-2">
                {roles.map((role, index) => (
                  <span
                    key={`${role}-${index}`}
                    className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-800"
                  >
                    <FaUsers className="text-xs" />
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Updated At
              </p>
              <p className="text-sm font-medium text-gray-800">
                {formatDate(scenario.updatedAt)} at {formatTime(scenario.updatedAt)}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Created At
              </p>
              <p className="text-sm font-medium text-gray-800">
                {formatDate(scenario.createdAt)} at {formatTime(scenario.createdAt)}
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export const AIScenariosTable: React.FC<Props> = ({ data }) => {
  const [viewTarget, setViewTarget] = useState<ScenarioRow | null>(null);

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[1080px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gradient-to-r from-indigo-900 to-slate-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Scenario ID</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">User</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold min-w-[380px]">Situation</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Roles</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Updated At</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Created At</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-8 text-xl text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  <FaRobot className="text-4xl text-gray-300" />
                  <span>No AI Custom Topics Found</span>
                </div>
              </td>
            </tr>
          )}

          {data.map((scenario) => (
            <tr
              key={scenario.id}
              className="text-center odd:bg-white even:bg-gradient-to-r even:from-indigo-50/40 even:to-slate-50/50 hover:bg-indigo-100/40 transition-colors duration-200"
            >
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <span className="font-mono text-[10px] sm:text-[11px] bg-gray-100 px-2 py-1 rounded text-gray-700 break-all">
                  {scenario.id}
                </span>
              </td>

              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <Link
                  to={`/users/${scenario.userId}`}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 px-3 py-1.5 text-[10px] sm:text-[11px] font-semibold text-white no-underline"
                >
                  <FaUser className="text-[10px]" />
                  {scenario.userId}
                </Link>
              </td>

              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200 min-w-[380px] max-w-[380px]">
                <div className="text-left">
                  <p className="text-[11px] sm:text-xs text-gray-700 leading-5" title={scenario.situation}>
                    {truncateText(scenario.situation)}
                  </p>
                </div>
              </td>

              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <div className="flex flex-wrap justify-center gap-1.5">
                  {scenario.roles?.slice(0, 2).map((role, index) => (
                    <span
                      key={`${role}-${index}`}
                      className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-800"
                    >
                      <FaUsers className="text-[8px]" />
                      {role}
                    </span>
                  ))}
                </div>
              </td>

              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">
                    {formatDate(scenario.updatedAt)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500">
                    {formatTime(scenario.updatedAt)}
                  </span>
                </div>
              </td>

              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">
                    {formatDate(scenario.createdAt)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500">
                    {formatTime(scenario.createdAt)}
                  </span>
                </div>
              </td>

              <td className="px-2 py-3 border-t border-gray-200">
                <MenuActions
                  actions={[
                    {
                      type: "BUTTON",
                      label: "View",
                      onClick: () => setViewTarget(scenario),
                      color: "primary",
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ScenarioDetailsModal scenario={viewTarget} onClose={() => setViewTarget(null)} />
    </div>
  );
};

export default AIScenariosTable;
